/**
 * Lot AW « Hors connexion » : lectures gardées sur le téléphone (liste blanche) et écrans utilisables sans réseau.
 *
 * Gardé : À faire, Projets, Agenda et les fiches R1 déjà ouvertes, et ce que ces écrans affichent autour (membres,
 * clients du sélecteur, canaux du R1, notifications, état des connexions). PAS gardé : les Chiffres, la
 * Trésorerie, les Dépenses (montants sensibles sur un téléphone perdu), les Réglages, l'assistant…
 */

/** Lectures sans personne (configuration de connexion, personne connectée), gardées sous une clé commune. */
export const GLOBAL_PATHS: readonly string[] = ['/api/auth/config', '/api/me'];

const PATTERNS: readonly RegExp[] = [
  /^\/api\/auth\/config$/,
  /^\/api\/me$/,
  /^\/api\/members$/,
  /^\/api\/actions(\?.*)?$/,
  /^\/api\/actions\/done(\?.*)?$/,
  /^\/api\/actions\/stakes(\?.*)?$/,
  /^\/api\/clients\/options$/,
  /^\/api\/clients\/no-subscription$/,
  /^\/api\/clients\/quote-followup-owners$/,
  /^\/api\/connections\/overview$/,
  /^\/api\/notifications$/,
  /^\/api\/acquisition\/channels$/,
  /^\/api\/projects$/,
  /^\/api\/projects\/order$/,
  /^\/api\/projects\/board$/,
  /^\/api\/projects\/load(\?.*)?$/,
  /^\/api\/projects\/[0-9a-f-]{36}$/,
  /^\/api\/agenda\/week(\?.*)?$/,
  /^\/api\/agenda\/upcoming$/,
  /^\/api\/r1\/[^/?]+$/,
];

/** Lecture gardée sur le téléphone ? */
export function isCacheable(path: string): boolean {
  return PATTERNS.some((re) => re.test(path));
}

/**
 * Clé d'une réponse gardée : par personne (`<id>|<chemin>`), sauf la configuration et la personne connectée (lues
 * avant de savoir qui est là). Les paramètres de la requête sont triés : `?a=1&b=2` = `?b=2&a=1`.
 */
export function cacheKey(path: string, owner: string | null): string | null {
  const [pathname = '', query = ''] = path.split('?');
  const params = new URLSearchParams(query);
  const sorted = [...params.entries()].sort(([a], [b]) => a.localeCompare(b));
  const normalized = sorted.length > 0 ? `${pathname}?${new URLSearchParams(sorted).toString()}` : pathname;
  if (GLOBAL_PATHS.includes(pathname)) return `*|${normalized}`;
  return owner ? `${owner}|${normalized}` : null;
}

/** Réponses gardées même à la déconnexion : la configuration de connexion (non personnelle). */
export const KEPT_ON_WIPE: readonly string[] = ['*|/api/auth/config'];

/** Écrans utilisables sans réseau (noms des routes) ; les autres affichent « Pas disponible hors connexion ». */
export const OFFLINE_SCREENS: readonly string[] = ['afaire', 'projets', 'agenda'];

export function isOfflineScreen(name: unknown): boolean {
  return typeof name === 'string' && OFFLINE_SCREENS.includes(name);
}

/** « 14 h 32 » (heure de Paris) ; « 17/10 à 14 h 32 » si ce n'est pas aujourd'hui. */
export function dataTimeLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const parts = (d: Date) =>
    Object.fromEntries(
      new Intl.DateTimeFormat('fr-FR', {
        timeZone: 'Europe/Paris',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(d)
        .map((p) => [p.type, p.value]),
    ) as Record<string, string>;
  const p = parts(date);
  const time = `${Number(p.hour)} h ${String(p.minute).padStart(2, '0')}`;
  const today = parts(now);
  return p.day === today.day && p.month === today.month ? time : `${p.day}/${p.month} à ${time}`;
}
